/// <amd-module name="Repos/Editor/Components/AnnotationList" />
import * as React from "react";
import { IAnnotationBlock } from "Repos/Common/Contracts/Editor";
import { getShortCommitComment } from "Repos/Common/Util/Commit";
import * as Resources from "Repos/Editor/Resources";
import { IReadonlyObservableValue, IReadyableReadonlyObservableArray, ObservableValue } from "VSS/Core/Observable";
import { ScreenSize } from "VSS/Core/Util/Screen";
import { dateToString } from "VSS/Core/Util/String";
import { FPSLink } from "VSS/Features/PlatformUI/FPSLink";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { Icon } from "VSSUI/Icon";
import { Observer } from "VSSUI/Observer";
import { IndeterminateProgressBar } from "VSSUI/ProgressBar";
import { Tooltip } from "VSSUI/TooltipEx";
import { ScreenSizeConditional } from "VSSUI/Utilities/ScreenSize";

export interface IAnnotationListProps {
    annotationBlocks: IReadyableReadonlyObservableArray<IAnnotationBlock>;
    initialScrollPosition?: { scrollLeft: number; scrollTop: number };
    lineHeight: number;
    height: number;
    onScroll?: (top: number) => void;
    selectedLine: IReadonlyObservableValue<number>;
}

/**
 * Element containing all annotations for this file.
 */
export class AnnotationList extends VssComponent<IAnnotationListProps> {
    private containerRef: HTMLDivElement | null;
    private selectedChangeId = new ObservableValue("");

    constructor(props: IAnnotationListProps, context: IVssComponentContext) {
        super(props, context);

        this.observe([
            {
                propertyName: "selectedLine",
                observer: this.onSelectedLineChanged
            }
        ]);
    }

    public render(): JSX.Element {
        return (
            <Observer blocks={this.props.annotationBlocks} ready={this.props.annotationBlocks.ready}>
                {(props: { blocks: IAnnotationBlock[]; ready: boolean }) => {
                    return (
                        <div
                            className="flex-column relative v-scroll-auto custom-scrollbar custom-scrollbar-hidden"
                            style={{ height: this.props.height }}
                            ref={ref => {
                                this.containerRef = ref;
                                if (ref && this.props.initialScrollPosition) {
                                    ref.scrollTo({
                                        top: this.props.initialScrollPosition.scrollTop,
                                        left: this.props.initialScrollPosition.scrollLeft
                                    });
                                }
                            }}
                            onScroll={this.onScroll}
                        >
                            {!props.ready && <IndeterminateProgressBar className="repos-annotations-loading-progress" loading={true} />}
                            <div className="flex-column">{props.blocks.map(this.renderBlock)}</div>
                        </div>
                    );
                }}
            </Observer>
        );
    }

    public getScrollTop(): number {
        return this.containerRef?.scrollTop || 0;
    }

    public scrollTo(scrollTop: number) {
        if (this.containerRef) {
            this.containerRef.scrollTop = scrollTop;
        }
    }

    private onSelectedLineChanged = (lineNumber: number): void => {
        let selectedChangeId = "";
        for (const block of this.props.annotationBlocks.value) {
            if (lineNumber >= block.startLine && lineNumber <= block.startLine + block.lineCount - 1) {
                if (block.historyEntry) {
                    selectedChangeId = block.historyEntry.shortId;
                }
                break;
            }
        }

        if (this.selectedChangeId.value !== selectedChangeId) {
            this.selectedChangeId.value = selectedChangeId;
        }
    };

    private onScroll = (event: React.SyntheticEvent<HTMLElement>) => {
        this.props.onScroll!((event.target as HTMLElement).scrollTop);
    };

    private renderBlock = (block: IAnnotationBlock) => {
        const key = `annotation-block-${block.startLine}`;
        return (
            <AnnotationBlock
                blockDetails={block}
                key={key}
                lineHeight={this.props.lineHeight}
                selectedChangeId={this.selectedChangeId}
                selectedLineNumber={this.props.selectedLine}
            />
        );
    };
}

interface IAnnotationBlockProps {
    blockDetails: IAnnotationBlock;
    lineHeight: number;
    selectedChangeId: IReadonlyObservableValue<string>;
    selectedLineNumber: IReadonlyObservableValue<number>;
}

/**
 * A single annotation block, which can span several lines.
 */
class AnnotationBlock extends VssComponent<IAnnotationBlockProps> {
    private isSelectedLine = new ObservableValue(false);
    private isSelectedChange = new ObservableValue(false);

    constructor(props: IAnnotationBlockProps) {
        super(props);

        this.observe([
            {
                propertyName: "selectedChangeId",
                observer: this.onSelectedChangeChanged
            },
            {
                propertyName: "selectedLineNumber",
                observer: this.onSelectedLineChanged
            }
        ]);

        this.onSelectedChangeChanged(props.selectedChangeId.value);
        this.onSelectedLineChanged(props.selectedLineNumber.value);
    }

    public render(): JSX.Element {
        const { historyEntry, lineCount } = this.props.blockDetails;
        const blockHeight = lineCount * this.props.lineHeight + "px";

        // @NOTE: Set minHeight for IPhone, otherwise it will ignore the height.

        return (
            <div
                className="repos-annotation-block padding-left-8 padding-right-4 body-s scroll-hidden"
                style={{ height: blockHeight, minHeight: blockHeight }}
            >
                <div className="repos-annotation-details flex-row flex-center">
                    <ScreenSizeConditional condition={screenSize => screenSize <= ScreenSize.xsmall}>
                        {historyEntry && historyEntry.authorName ? (
                            <FPSLink className="repos-annotation-mobile repos-annotation-cell flex-row flex-wrap" href={historyEntry.changeUrl}>
                                <span className="margin-right-4">{historyEntry.authorName}</span>
                                <span>{dateToString(historyEntry.date, "date")}</span>
                            </FPSLink>
                        ) : null}
                    </ScreenSizeConditional>
                    <ScreenSizeConditional condition={screenSize => screenSize > ScreenSize.xsmall}>
                        <>
                            <Observer isSelectedLine={this.isSelectedLine} isSelectedChange={this.isSelectedChange}>
                                {(selectedProps: { isSelectedLine: boolean; isSelectedChange: boolean }) => {
                                    if (selectedProps.isSelectedLine) {
                                        return <div className="repos-annotation-line-selected repos-annotation-cell" />;
                                    } else if (selectedProps.isSelectedChange) {
                                        return <div className="repos-annotation-commit-selected repos-annotation-cell" />;
                                    } else {
                                        return null;
                                    }
                                }}
                            </Observer>
                            {(historyEntry && (
                                <FPSLink
                                    className="repos-annotation-version repos-annotation-cell margin-right-4 monospaced-text white-space-nowrap"
                                    href={historyEntry.changeUrl}
                                    tooltipProps={{ text: getShortCommitComment(historyEntry.comment || "") }}
                                >
                                    {historyEntry.shortId}
                                </FPSLink>
                            )) || <div className="repos-annotation-version repos-annotation-cell margin-right-4 invisible"></div>}
                            {(historyEntry && historyEntry.annotatePreviousUrl && (
                                <FPSLink
                                    ariaLabel={Resources.PriorBlame}
                                    className="repos-annotation-prior repos-annotation-cell white-space-nowrap"
                                    href={historyEntry.annotatePreviousUrl}
                                    tooltipProps={{ text: Resources.PriorBlame }}
                                >
                                    <Icon iconName="Rewind" />
                                </FPSLink>
                            )) || <div className={"repos-annotation-prior repos-annotation-cell invisible"}></div>}
                            {(historyEntry && historyEntry.authorName && (
                                <Tooltip
                                    text={
                                        historyEntry.authorEmail
                                            ? `${historyEntry.authorName} <${historyEntry.authorEmail}>`
                                            : historyEntry.authorName
                                    }
                                >
                                    <div className="repos-annotation-author repos-annotation-cell flex-grow text-ellipsis">
                                        {historyEntry.authorName}
                                    </div>
                                </Tooltip>
                            )) || <div className="repos-annotation-author repos-annotation-cell invisible"></div>}
                            {(historyEntry && historyEntry.date && (
                                <Tooltip text={dateToString(historyEntry.date, "dateTime")}>
                                    <div className="repos-annotation-date repos-annotation-cell white-space-nowrap">
                                        {dateToString(historyEntry.date, "date")}
                                    </div>
                                </Tooltip>
                            )) || <div className="repos-annotation-date repos-annotation-cell invisible"></div>}
                        </>
                    </ScreenSizeConditional>
                </div>
            </div>
        );
    }

    private onSelectedChangeChanged = (selectedChangeId: string) => {
        const block = this.props.blockDetails;
        const isSelected = block.historyEntry && block.historyEntry.shortId === selectedChangeId;
        if (!!isSelected !== this.isSelectedChange.value) {
            this.isSelectedChange.value = !!isSelected;
        }
    };

    private onSelectedLineChanged = (selectedLine: number) => {
        const block = this.props.blockDetails;
        const isSelectedLine = selectedLine >= block.startLine && selectedLine <= block.startLine + block.lineCount - 1;
        if (!!isSelectedLine !== this.isSelectedLine.value) {
            this.isSelectedLine.value = !!isSelectedLine;
        }
    };
}
